import React from 'react';
import PropTypes from 'prop-types';
import { GlobalState, Select } from '@leverege/ui-redux';
import { ValuesCache } from '@leverege/value-cache';
import { ResourceUserReducer, AllowedReducer, ApiProvider, Auth } from '@leverege/api-redux';
import { Dialogs, Table, List, Content } from '@leverege/ui-elements';
import { Toolbar, TitleBar, createConnect } from '@leverege/ui-plugin';
import I18N from '@leverege/i18n';
import { getColumns } from "./ResourceUsersColumns.js";
import Paginator from "./Paginator.js";
import TablePagination from "./TablePagination.js";
import Icons from "./Icons.js";
import S from './ResourceUsersScreen.css';
import ResendPasswordLoading from "./user/ResendPasswordLoading.js";
import { USER_INVITE_OPTIONS_KEY, getUiNameCalculator } from "./temporaryUser/Util.js";
const {
  t
} = I18N.ns('userManagement.ResourceUsersScreen');
class ResourceUsersScreen extends React.Component {
  static CLIENT = 'ResourceUsers';
  static ACTION_MATCH = {
    use: 'actionBar',
    client: ResourceUsersScreen.CLIENT
  };
  static CONTEXT_MATCH = {
    use: 'contextMenu',
    client: ResourceUsersScreen.CLIENT
  };
  static TABLE_POST_COLUMN_MATCH = {
    use: 'postActionsColumnBar',
    client: ResourceUsersScreen.CLIENT
  };
  static TABLE_FOOTER_MATCH = {
    use: 'actionsFooterBar',
    client: ResourceUsersScreen.CLIENT
  };
  static propTypes = {
    users: PropTypes.array,
    title: PropTypes.string,
    showHeader: PropTypes.bool,
    calcUiName: PropTypes.oneOfType([PropTypes.bool, PropTypes.func])
  };
  static defaultProps = {
    users: [],
    get title() {
      return t('title');
    },
    showHeader: true,
    calcUiName: true
  };
  static contextType = ApiProvider.Context;
  constructor(props) {
    super(props);
    this.cache = new ValuesCache();
    this.state = {
      resent: {}
    };
    this.selector = new List.SingleSelector(this, 'selection', {
      idKey: 'id'
    });
    this.paginator = new Paginator({
      onPageChange: this.onSetPage
    });
  }
  getContext(optionalUser) {
    const {
      targeted,
      actions,
      resourceId,
      api,
      emailOptions,
      smsOptions,
      uiName = process.env.userManagementUiName,
      systemId,
      calcUiName
    } = this.props;
    const context = {
      target: optionalUser || targeted,
      targetType: 'user',
      client: 'ResourceUsers',
      resourceId,
      actions,
      api,
      emailOptions,
      smsOptions,
      uiName: calcUiName === true ? getUiNameCalculator(uiName, {
        optionsKey: USER_INVITE_OPTIONS_KEY
      }) : typeof calcUiName === 'function' ? calcUiName : uiName,
      clientProps: this.props,
      systemId
    };
    return context;
  }
  componentDidMount() {
    const {
      dispatch,
      actions,
      usersState
    } = this.props;
    dispatch(Select.clear('resourceUsersTargeted'));
    if (ResourceUserReducer.needsLoading(usersState, undefined, 100)) {
      dispatch(actions.users().list({
        offset: 0,
        limit: 100,
        order: ['userId']
      }));
    }
  }
  componentWillUnmount() {
    this.props.dispatch(Select.clear('resourceUsersTargeted')); // eslint-disable-line react/destructuring-assignment
  }
  onSetPage = ({
    page,
    perPage
  }) => {
    const {
      dispatch,
      actions
    } = this.props;
    dispatch(actions.users().list({
      offset: page * perPage,
      limit: perPage,
      order: ['userId']
    }));
  };
  onResend = ({
    data
  }) => {
    const {
      projectId,
      systemId,
      uiName,
      calcUiName
    } = this.props;
    const {
      resent
    } = this.state;
    let resolvedUiName = uiName || process.env.userManagementUiName;
    if (calcUiName) {
      let calculator;
      if (typeof calcUiName === 'function') {
        calculator = calcUiName;
      } else {
        calculator = getUiNameCalculator(resolvedUiName, {
          optionsKey: USER_INVITE_OPTIONS_KEY
        });
      }
      resolvedUiName = calculator({
        user: data
      });
    }
    const p = GlobalState.dispatch(Auth.forgotPassword(data.username, projectId, {
      uiName: resolvedUiName,
      emailOptions: {
        systemId
      },
      smsOptions: {
        systemId
      }
    }));
    Dialogs.show({
      component: ResendPasswordLoading,
      props: {
        promise: p,
        message: t('message')
      }
    });
    p.then(res => {
      this.setState({
        resent: {
          ...resent,
          [data.id]: true
        }
      });
    });
  };
  onCheckAll = () => {
    const {
      targeted,
      users,
      dispatch
    } = this.props;
    if (Select.isEmpty(targeted)) {
      const ids = users.map(e => e.id);
      dispatch(Select.set(ids, 'resourceUsersTargeted'));
    } else {
      dispatch(Select.clear('resourceUsersTargeted'));
    }
  };
  onCheck = ({
    data
  }) => {
    const {
      dispatch
    } = this.props;
    dispatch(Select.toggle(data.id, 'resourceUsersTargeted'));
  };
  onFilter = event => {
    this.setState({
      [event.data]: event.value
    });
  };
  onUserSelected = evt => {
    const {
      dispatch,
      actions
    } = this.props;
    const user = evt.value;
    dispatch(actions.user(user.id).get({
      includeRoles: true
    }));
  };
  createDataItems = (items, offset, limit, count, status) => {
    return {
      status: status || null,
      loading: status === 'loading',
      page: Math.ceil(offset / limit) || 0,
      perPage: limit || 100,
      count: count || 0,
      items: items || []
    };
  };
  filterPageItems = paginator => {
    const {
      selectorProps
    } = this.props;
    return this.cache.calc(`page-${paginator.page}`, pageItems => {
      let items = pageItems;
      if (selectorProps.simple) {
        // Only show custom roles
        items = pageItems.map(item => {
          const nItem = {
            ...item,
            roles: item.roles.filter(role => role.scope === 'custom')
          };
          return nItem;
        });
      }
      return items;
    }, paginator.pageItems);
  };
  render() {
    const {
      resent
    } = this.state;
    const {
      users,
      offset,
      limit,
      count,
      status,
      targeted,
      targetedCount,
      title,
      showHeader,
      variants
    } = this.props;
    const postActionsToolbar = /*#__PURE__*/React.createElement(Toolbar, {
      variant: variants?.toolbar || 'actionBar',
      prefer: "icon",
      matchContext: ResourceUsersScreen.TABLE_POST_COLUMN_MATCH,
      context: this.getContext()
    });
    const columns = getColumns({
      targeted,
      targetedCount,
      users,
      postActionsToolbar,
      onCheck: this.onCheck,
      onCheckAll: this.onCheckAll,
      onResend: this.onResend,
      resent
    });
    const dataItems = this.cache.calc('items', this.createDataItems, users, offset, limit, count, status);
    this.paginator.setData(dataItems);
    return /*#__PURE__*/React.createElement(Content, null, /*#__PURE__*/React.createElement(Content.Area, null, /*#__PURE__*/React.createElement(Content, null, showHeader ? /*#__PURE__*/React.createElement(Content.Header, null, /*#__PURE__*/React.createElement(TitleBar, {
      icon: Icons.screenIcon,
      title: title,
      variant: "screenTitle"
    }, /*#__PURE__*/React.createElement(Toolbar, {
      variant: variants?.toolbar || 'actionBar',
      prefer: "icon",
      hasContextMenu: true,
      contextMenuHideWhenEmpty: false,
      matchContext: ResourceUsersScreen.ACTION_MATCH,
      context: this.getContext()
    }))) : null, /*#__PURE__*/React.createElement(Content.Area, null, /*#__PURE__*/React.createElement(Table, {
      variant: variants?.table,
      className: S.table,
      manual: true,
      sortable: true,
      pageSize: Math.min(limit, users.length),
      showPagination: false,
      showPageSizeOptions: false,
      showPaginationBottom: false,
      columns: columns,
      data: this.paginator.pageItems,
      loading: this.paginator.loading,
      noDataText: t('noDataText')
    })), /*#__PURE__*/React.createElement(Content.Footer, null, /*#__PURE__*/React.createElement(TablePagination, {
      context: this.getContext(),
      matchContext: ResourceUsersScreen.TABLE_FOOTER_MATCH,
      paginator: this.paginator
    })))));
  }
}
const connect = createConnect(ResourceUsersScreen.CLIENT, {
  client: ResourceUsersScreen.CLIENT
});
export default connect((state, props) => {
  const {
    resourceId
  } = props;
  return {
    allowed: AllowedReducer.get(state, resourceId),
    usersState: ResourceUserReducer.get(state, resourceId),
    users: ResourceUserReducer.getUsers(state, resourceId),
    count: ResourceUserReducer.getUserCount(state, resourceId),
    limit: ResourceUserReducer.getUserLimit(state, resourceId),
    offset: ResourceUserReducer.getUserOffset(state, resourceId),
    status: ResourceUserReducer.getStatus(state, resourceId),
    error: ResourceUserReducer.getError(state, resourceId),
    targeted: Select.get(state.select, 'resourceUsersTargeted'),
    targetedCount: Select.count(state.select, 'resourceUsersTargeted')
  };
}, null, null, {
  forwardRef: true
})(ResourceUsersScreen);